import { FC } from 'react';
import { withStyles, Card, CportalServiceItem, CportalService, Box, LoadingIndicator } from 'vopay';
import { useTranslation } from 'react-i18next';
import { Title } from 'UI';
import ServiceToggle from './ServiceToggle';
import styles from './Services.module.scss';

const cn = withStyles(styles);

type ServicesProps = {
  isCportalEnabled: boolean;
  clientAccountServices: CportalServiceItem[];
  accountServices: CportalServiceItem[];
  isFetchingServices: boolean;
  onToggleService: (service: CportalService, enabled: boolean) => Promise<void>;
};

const Services: FC<ServicesProps> = ({
  isCportalEnabled,
  clientAccountServices,
  accountServices,
  isFetchingServices,
  onToggleService,
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Customers.CportalTab' });

  // A service is editable only when the account offers it (account-level service
  // enabled — i.e. at least one backing rail permitted). Others are read-only.
  const accountAllows = new Set(accountServices.filter((s) => s.Enabled).map((s) => s.Service));

  const defaultServices: CportalServiceItem[] = [
    { Service: 'Fund', Enabled: false },
    { Service: 'Transfer', Enabled: false },
    { Service: 'Withdraw', Enabled: false },
    { Service: 'Global Send', Enabled: false },
  ];

  const displayedServices =
    isCportalEnabled && clientAccountServices?.length ? clientAccountServices : defaultServices;

  const serviceOrder = ['Fund', 'Withdraw', 'Transfer', 'Global Send'];

  const isLoadingServices = isCportalEnabled && isFetchingServices && !clientAccountServices.length;

  const serviceLabelMap: Record<CportalService, string> = {
    Fund: t('fundWallet', { defaultValue: 'Fund wallets' }),
    Withdraw: t('withdrawWallet', { defaultValue: 'Withdraw from wallets' }),
    Transfer: t('transferWallet', { defaultValue: 'Transfer between wallets' }),
    'Global Send': t('globalSend', { defaultValue: 'Global Send' }),
  };

  return (
    <div className={cn('container')}>
      <Box flx="col" gapSize="2px" padding="16px 0 0 12px">
        <Title level="6">{t('services', { defaultValue: 'Services' })}</Title>

        <div className={cn('description')}>
          {t('servicesDescription', { defaultValue: 'Manage services related to cPortal account.' })}
        </div>
      </Box>

      <Card className={cn('serviceContainer')} hoverEffect={false}>
        {isLoadingServices ? (
          <LoadingIndicator isVisible size="md" hasNoMessage hasNoTitle />
        ) : (
          displayedServices
            .filter((service) => service.Service !== 'Global Send')
            .sort((a, b) => serviceOrder.indexOf(a.Service) - serviceOrder.indexOf(b.Service))
            .map((service) => (
              <div key={service.Service} className={cn('toggleContainer')}>
                <ServiceToggle
                  service={service.Service}
                  checked={service.Enabled}
                  readOnly={!isCportalEnabled || !accountAllows.has(service.Service)}
                  onToggle={onToggleService}
                  label={serviceLabelMap[service.Service]}
                />
              </div>
            ))
        )}
      </Card>
    </div>
  );
};

export default Services;
